import Highcharts from "highcharts/esm/highcharts.src"

import { useCallback, useState } from "react"

import { colors, colorScale, Text } from "@minuthq/meatball-ui-react"

import { GraphCard } from "src/components/Homes/DeviceDetails/Overview/GraphCard"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { filterAnalyticsByHomegroup } from "src/data/portfolioAnalytics/logic/filterAnalyticsByHomegroup"
import { getWifiHealthCoverage } from "src/data/portfolioAnalytics/logic/portfolioCoverage"
import { useFetchWifiHealthByOrganization } from "src/data/portfolioAnalytics/portfolioAnalyticsQueries"
import {
  TDeviceWifiHealthAnalytics,
  TWifiHealthAnalyticsData,
} from "src/data/portfolioAnalytics/types/portfolioAnalyticsTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useUrlParam } from "src/hooks/useUrlParam"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { BarChart } from "src/ui/Graphs/BarChart"
import { TBarChartMultiSeriesData } from "src/ui/Graphs/graphTypes"
import { getPercentage } from "src/utils/genericUtil"
import { getStoredSelectedTimezone } from "src/utils/l10n"

import { PortfolioAnalyticsWifiHealthTooltipContainer } from "./PortfolioAnalyticsWifiHealthTooltip"

const GRAPH_HEIGHT = 300

export function PortfolioAnalyticsWifiHealthGraph() {
  const { t } = useTranslate()
  const user = useGetUser()
  const timezone = getStoredSelectedTimezone()
  const { orgId } = useOrganization()
  const { value: homegroupIds } = useUrlParam("homegroup_ids")
  const selectedHomegroupIds = homegroupIds ? homegroupIds.split(",") : []

  const [chart, setChart] = useState<Highcharts.Chart | null>(null)
  const callback = useCallback((chartInstance: Highcharts.Chart) => {
    setChart(chartInstance)
  }, [])

  const fetchWifiHealthByOrganization = useFetchWifiHealthByOrganization({
    orgId,
  })

  const analytics = fetchWifiHealthByOrganization.data?.analytics

  // Filter analytics by selected homegroups
  const filteredAnalytics = filterAnalyticsByHomegroup(
    analytics,
    selectedHomegroupIds
  )

  const { isLoading } = fetchWifiHealthByOrganization

  const { excellent, good, fair, poor, none, noDevices } =
    getWifiHealthCoverage(
      filteredAnalytics?.flatMap((analytics) => analytics.data) ?? []
    )

  function hasNonZeroTotal(item: TWifiHealthAnalyticsData) {
    const total =
      item.aggregation.excellent +
      item.aggregation.good +
      item.aggregation.fair +
      item.aggregation.poor +
      item.aggregation.none +
      item.aggregation.no_devices
    return total > 0
  }

  const wifiHealthData: TBarChartMultiSeriesData[] = [
    {
      name: t(langKeys.signal_excellent),
      data:
        filteredAnalytics?.flatMap(
          (analyticsGroup: TDeviceWifiHealthAnalytics) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map((item: TWifiHealthAnalyticsData) => ({
                id: item.grouped_by,
                name: item.metadata.homegroup_name,
                y: item.aggregation.excellent,
              }))
        ) ?? [],

      fillColor: colorScale.good[200],
      borderColor: colorScale.good[400],
      legendSymbolColor: colorScale.good[400],
    },
    {
      name: t(langKeys.signal_good),
      data:
        filteredAnalytics?.flatMap(
          (analyticsGroup: TDeviceWifiHealthAnalytics) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map((item: TWifiHealthAnalyticsData) => ({
                id: item.grouped_by,
                name: item.metadata.homegroup_name,
                y: item.aggregation.good,
              }))
        ) ?? [],

      fillColor: colors.systemGoodLight,
      borderColor: colors.systemGoodDark,
      legendSymbolColor: colors.systemGoodDark,
    },
    {
      name: t(langKeys.signal_fair),
      data:
        filteredAnalytics?.flatMap(
          (analyticsGroup: TDeviceWifiHealthAnalytics) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map((item: TWifiHealthAnalyticsData) => ({
                id: item.grouped_by,
                name: item.metadata.homegroup_name,
                y: item.aggregation.fair,
              }))
        ) ?? [],
      fillColor: colors.systemWarningLight,
      borderColor: colors.systemWarningDark,
      legendSymbolColor: colors.systemWarningDark,
    },
    {
      name: t(langKeys.signal_weak),
      data:
        filteredAnalytics?.flatMap(
          (analyticsGroup: TDeviceWifiHealthAnalytics) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map((item: TWifiHealthAnalyticsData) => ({
                id: item.grouped_by,
                name: item.metadata.homegroup_name,
                y: item.aggregation.poor,
              }))
        ) ?? [],
      fillColor: colors.systemErrorLight,
      borderColor: colors.systemErrorDark,
      legendSymbolColor: colors.systemErrorDark,
    },
    {
      name: t(langKeys.signal_none),
      data:
        filteredAnalytics?.flatMap(
          (analyticsGroup: TDeviceWifiHealthAnalytics) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map((item: TWifiHealthAnalyticsData) => ({
                id: item.grouped_by,
                name: item.metadata.homegroup_name,
                y: item.aggregation.none,
              }))
        ) ?? [],
      fillColor: colorScale.error[200],
      borderColor: colorScale.error[400],
      legendSymbolColor: colorScale.error[400],
    },
    {
      name: t(langKeys.no_devices),
      data:
        filteredAnalytics?.flatMap(
          (analyticsGroup: TDeviceWifiHealthAnalytics) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map((item: TWifiHealthAnalyticsData) => ({
                id: item.grouped_by,
                name: item.metadata.homegroup_name,
                y: item.aggregation.no_devices,
              }))
        ) ?? [],
      fillColor: colors.neutralDark,
      borderColor: colors.neutralDark2,
      legendSymbolColor: colors.neutralDark2,
    },
  ]

  return (
    <GraphCard
      title={t(langKeys.portfolio_analytics_wifi_health_title)}
      titleVariant="heading3"
      info={t(langKeys.portfolio_analytics_wifi_health_info)}
      rightInfo={
        <Text variant="heading3">
          {getPercentage(
            excellent + good + fair,
            excellent + good + fair + poor + none + noDevices
          )}
          %
        </Text>
      }
      isLoading={isLoading}
    >
      <BarChart
        dataSeriesType="multi"
        data={wifiHealthData}
        timezone={timezone.timezone.tzCode}
        stackSeries
        clockType={user.clock_type}
        height={GRAPH_HEIGHT}
        borderRadius={2}
        seriesHoverBrightness={0.03}
        interactive
        callback={callback}
        yAxisOptions={{
          title: {
            text: t(langKeys.number_of_units),
          },
        }}
        xAxisOptions={{
          type: "category",
        }}
        tooltip={{
          decimals: 0,
          unit: "",
          stickOnContact: true,
        }}
      />
      <PortfolioAnalyticsWifiHealthTooltipContainer
        chart={chart}
        series={chart?.userOptions.series}
      />
    </GraphCard>
  )
}
